// components
import Image from 'next/image';
import YoutubeVideo from '@/components/utilities/YoutubeVideo';
import VisibleLoading from '@/components/utilities/VisibleLoading';

// utilities
import { getImagePath } from '@/utils/images';

const TestimonialsBlock = ({ testimonialsBlockContent: content }) => {
  return (
    <div className="mx-auto mt-20 mb-20 w-[90vw] xl:w-(--container-width)">
      <div className="-mx-3 flex flex-wrap gap-y-6">
        {(content || []).map((item, index) => (
          <TestimonialItem key={item.id} index={index} testimonial={item} />
        ))}
      </div>
    </div>
  );
};
export default TestimonialsBlock;

export const TestimonialItem = ({ testimonial, index }) => {
  let quoteColor = 'fill-brand-400';
  if (testimonial.color === 'blue') {
    quoteColor = 'fill-blue-400';
  } else if (testimonial.color === 'green') {
    quoteColor = 'fill-green-500';
  } else if (testimonial.color === 'purple') {
    quoteColor = 'fill-purple-500';
  }

  if (!testimonial?.testimonial) {
    return null;
  }

  return testimonial.testimonial.video ? (
    <div
      className={`flex basis-full flex-wrap justify-between gap-y-8 px-3 py-12 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
    >
      <VisibleLoading delay={300} direction={index % 2 === 0 ? 'left' : 'right'}>
        {({ ref, className }) => (
          <div ref={ref} className={`max-w-[494px] ${className || ''}`.trim()}>
            <div>
              <div className="mb-8 h-[28px] text-[135px]">
                <svg width="39" height="28" viewBox="0 0 39 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M16.84 20.025C16.84 24.48 13.735 27.855 9.01 27.855C4.285 27.855 0.235001 24.075 0.235001 17.325C0.235001 8.95499 6.58 1.8 14.815 0.449997V5.445C10.09 6.38999 6.58 9.765 6.58 13.68C7.39 13.14 8.47 12.735 10.225 12.735C13.735 12.735 16.84 15.3 16.84 20.025ZM38.71 20.025C38.71 24.48 35.47 27.855 30.745 27.855C26.155 27.855 21.97 24.075 21.97 17.325C21.97 8.95499 28.315 1.8 36.685 0.449997V5.445C31.96 6.38999 28.45 9.765 28.45 13.545C29.26 13.005 30.475 12.735 32.095 12.735C35.605 12.735 38.71 15.3 38.71 20.025Z"
                    className={quoteColor}
                  />
                </svg>
              </div>
              <div className="txt-xl text-black">{testimonial.testimonial.message}</div>
            </div>
            <div className="mt-8 flex items-center gap-3">
              <div className="h-12 w-12 overflow-hidden rounded-full bg-white/[20%]">
                {testimonial.testimonial.image && (
                  <Image
                    src={getImagePath(testimonial.testimonial.image)}
                    alt={testimonial.testimonial.name}
                    width={testimonial.testimonial.image.width}
                    height={testimonial.testimonial.image.height}
                    sizes="48px"
                    className="h-full w-full object-cover"
                  />
                )}
              </div>
              <div>
                <div className="txt-xl font-medium text-black">{testimonial.testimonial.name}</div>
                <div className="txt-lg text-black-50">{testimonial.testimonial.company}</div>
              </div>
            </div>
          </div>
        )}
      </VisibleLoading>

      <VisibleLoading delay={500} direction={index % 2 !== 0 ? 'left' : 'right'}>
        {({ ref, className }) => (
          <div
            ref={ref}
            className={`min-h-[290px] w-full overflow-hidden rounded-2xl bg-black/5 xl:max-h-[290px] xl:max-w-[515px] ${className || ''}`.trim()}
          >
            <YoutubeVideo
              videoId={testimonial.testimonial.video.videoId}
              thumbnail={
                testimonial.testimonial.video.image ? (
                  <Image
                    src={getImagePath(testimonial.testimonial.video.image)}
                    alt={testimonial.testimonial.name}
                    fill={true}
                    className="object-cover"
                  />
                ) : null
              }
            />
          </div>
        )}
      </VisibleLoading>
    </div>
  ) : (
    <VisibleLoading delay={index % 2 === 0 ? 300 : 500}>
      {({ ref, className }) => (
        <div ref={ref} className={`justify-between px-3 lg:basis-1/2 ${className || ''}`.trim()}>
          <div className="flex h-full flex-col rounded-3xl bg-white p-10 shadow-2xl/15">
            <div className="mb-8 h-[28px] text-[135px]">
              <svg width="39" height="28" viewBox="0 0 39 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M16.84 20.025C16.84 24.48 13.735 27.855 9.01 27.855C4.285 27.855 0.235001 24.075 0.235001 17.325C0.235001 8.95499 6.58 1.8 14.815 0.449997V5.445C10.09 6.38999 6.58 9.765 6.58 13.68C7.39 13.14 8.47 12.735 10.225 12.735C13.735 12.735 16.84 15.3 16.84 20.025ZM38.71 20.025C38.71 24.48 35.47 27.855 30.745 27.855C26.155 27.855 21.97 24.075 21.97 17.325C21.97 8.95499 28.315 1.8 36.685 0.449997V5.445C31.96 6.38999 28.45 9.765 28.45 13.545C29.26 13.005 30.475 12.735 32.095 12.735C35.605 12.735 38.71 15.3 38.71 20.025Z"
                  className={quoteColor}
                />
              </svg>
            </div>
            <div className="txt-xl flex-1 text-black">{testimonial.testimonial.message}</div>
            <div className="mt-8 flex items-center gap-3">
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-white/[20%]">
                {testimonial.testimonial.image && (
                  <Image
                    src={getImagePath(testimonial.testimonial.image)}
                    alt={testimonial.testimonial.name}
                    width={testimonial.testimonial.image.width}
                    height={testimonial.testimonial.image.height}
                    sizes="48px"
                    className="h-full w-full object-cover"
                  />
                )}
              </div>
              <div className="flex-1">
                <div className="txt-xl font-medium text-black">{testimonial.testimonial.name}</div>
                <div className="txt-lg text-black-50">{testimonial.testimonial.company}</div>
              </div>

              {testimonial.testimonial.companyLogo && (
                <div className="max-w-[130px]">
                  <Image
                    src={getImagePath(testimonial.testimonial.companyLogo)}
                    alt={testimonial.testimonial.company}
                    width={testimonial.testimonial.companyLogo.width}
                    height={testimonial.testimonial.companyLogo.height}
                    sizes="130px"
                    className="h-6 object-contain object-right"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </VisibleLoading>
  );
};
